/* eslint-disable react-refresh/only-export-components */
import { CheckCircle2, X, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type ToastKind = "success" | "error";
type Toast = { id: number; kind: ToastKind; message: string };
type ToastContextValue = { success: (message: string) => void; error: (message: string) => void };

const ToastContext = createContext<ToastContextValue | null>(null);
let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);
  const push = useCallback((kind: ToastKind, message: string) => {
    const id = ++nextId;
    setToasts((current) => [...current, { id, kind, message }]);
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);
  const value = useMemo<ToastContextValue>(() => ({
    success: (message) => push("success", message),
    error: (message) => push("error", message)
  }), [push]);

  return <ToastContext.Provider value={value}>
    {children}
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[100] grid justify-items-center gap-2 px-4 sm:inset-x-auto sm:right-5 sm:top-5 sm:justify-items-end">
      {toasts.map((toast) => <div key={toast.id} role="status" className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-[10px] bg-cocoa px-4 py-3 text-white shadow-soft [animation:place-in_.35s_cubic-bezier(.2,.8,.2,1)]">
        <span className={`mt-0.5 shrink-0 ${toast.kind === "success" ? "text-[#9FD8B9]" : "text-blush"}`}>
          {toast.kind === "success" ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
        </span>
        <p className="flex-1 text-[15px] font-medium leading-snug">{toast.message}</p>
        <button onClick={() => dismiss(toast.id)} aria-label="Dismiss notification" className="-m-1 grid size-7 place-items-center rounded-full text-white/50 hover:text-white"><X size={15} /></button>
      </div>)}
    </div>
  </ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
}
